import type {ComponentProps} from 'react'
import {ActionMenu, Button, Image} from '@primer/react-brand/esm'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {findAllByContentType, type GenericGroup} from '../../../../../brand/lib/types/contentful'

import styles from './LogoActionMenu.module.css'

// IDE Icons
import EclipseIcon from '../../_assets/ide-icons/eclipse.svg'
import GitHubIcon from '../../_assets/ide-icons/github.svg'
import JetBrainsIcon from '../../_assets/ide-icons/jetbrains-logo.svg'
import RaycastIcon from '../../_assets/ide-icons/raycast-logo.svg'
import SqlServerManagementStudioIcon from '../../_assets/ide-icons/sql-server-management-studio-logo.svg'
import NeovimIcon from '../../_assets/ide-icons/neovim.svg'
import VisualStudioIcon from '../../_assets/ide-icons/visualstudio.webp'
import VSCodeIcon from '../../_assets/ide-icons/vscode.svg'
import XcodeIcon from '../../_assets/ide-icons/xcode.svg'
import ZedIcon from '../../_assets/ide-icons/zed.svg'

// App Icons
import SlackIcon from '../../_assets/app-icons/slack.svg'
import TeamsIcon from '../../_assets/app-icons/teams.svg'

const iconMap: Record<string, string> = {
  eclipse: EclipseIcon,
  github: GitHubIcon,
  jetbrains: JetBrainsIcon,
  raycast: RaycastIcon,
  sqlServerManagementStudio: SqlServerManagementStudioIcon,
  neovim: NeovimIcon,
  visualStudio: VisualStudioIcon,
  vscode: VSCodeIcon,
  xcode: XcodeIcon,
  zed: ZedIcon,
  slack: SlackIcon,
  teams: TeamsIcon,
}

// Id of the Contentful item that supplies the single button shown on small screens.
const MOBILE_ITEM_ID = 'showOnMobile'

const getOptionAnalytics = (action: string, location: string) =>
  getAnalyticsEvent({action, tag: 'button', context: 'CTAs', location})

type Props = {
  component: GenericGroup
  location: string
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
}

export function LogoActionMenu({component, location, variant = 'secondary'}: Props) {
  const contentItems = findAllByContentType(component.fields.content, 'genericContent')
  const mobileLink = contentItems.find(item => item.fields.id === MOBILE_ITEM_ID)?.fields.link?.fields
  const allItems = contentItems.filter(item => item.fields.id !== MOBILE_ITEM_ID)
  const items = allItems.flatMap(item => {
    const link = item.fields.link?.fields
    return link ? [{item, link}] : []
  })
  const firstLink = items[0]?.link

  if (!firstLink) return null

  // Without a dedicated mobile item, small screens get the first menu link.
  const buttonLink = mobileLink ?? firstLink

  // The first item's heading is used as the button prefix
  const buttonPrefix = allItems[0]?.fields.heading ?? 'Open in'

  return (
    <>
      {/* Desktop */}
      <div className={styles.actionMenu}>
        <ActionMenu mode="split-button">
          <ActionMenu.Button
            as="a"
            href={firstLink.href}
            variant={variant}
            {...getOptionAnalytics(firstLink.text, location)}
          >
            {buttonPrefix} {firstLink.text}
          </ActionMenu.Button>

          <ActionMenu.Overlay aria-label="Open in IDE">
            {items.map(({item, link}) => {
              const iconKey = item.fields.id
              const icon = iconKey ? iconMap[iconKey] : undefined

              return (
                <ActionMenu.Item key={item.sys.id} as="a" href={link.href} {...getOptionAnalytics(link.text, location)}>
                  {icon && <Image src={icon} alt="" width="20" height="20" loading="lazy" className={styles.icon} />}
                  {link.text}
                </ActionMenu.Item>
              )
            })}
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>

      {/* Mobile */}
      <Button
        as="a"
        href={buttonLink.href}
        variant={variant}
        {...getOptionAnalytics(buttonLink.text, location)}
        className={styles.mobileButton}
      >
        {buttonLink.text}
      </Button>
    </>
  )
}
